import React, { useState } from 'react';
import type { FeedbackConversationSummary } from '../../types/feedbackChat';

/**
 * Earlier conversations, collapsed by default.
 *
 * Collapsed because the primary action on this page is "say something", and a
 * list of past threads above the composer would push that below the fold on a
 * phone. Members who want to re-read something will open it; members with
 * feedback to give should not have to scroll past their own history first.
 */

interface Props {
  conversations: FeedbackConversationSummary[];
  activeId: string | null;
  onOpen: (id: string) => void;
  onStartNew: () => void;
}

export default function FeedbackConversationHistory({
  conversations, activeId, onOpen, onStartNew,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  if (!conversations.length) return null;

  return (
    <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--melio-border)' }}>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm font-medium"
          style={{ color: 'var(--melio-text-muted)' }}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide' : 'Show'} earlier conversations ({conversations.length})
        </button>

        <button
          type="button"
          onClick={onStartNew}
          className="text-sm underline underline-offset-4"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          Start a new one
        </button>
      </div>

      {expanded && (
        <ul className="mt-3 flex flex-col gap-2" style={{ listStyle: 'none', padding: 0 }}>
          {conversations.map((conversation) => {
            const isActive = conversation.id === activeId;
            return (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => onOpen(conversation.id)}
                  className="w-full text-left rounded-2xl px-3.5 py-2.5 transition-colors"
                  style={{
                    backgroundColor: isActive ? 'var(--melio-olive-soft)' : '#FFFFFF',
                    border: '1px solid var(--melio-border)',
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm" style={{ color: 'var(--melio-ink)' }}>
                      {formatDate(conversation.lastMessageAt)}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
                      {conversation.userMessageCount}
                      {conversation.userMessageCount === 1 ? ' message' : ' messages'}
                      {conversation.escalationState !== 'none' && ' · flagged'}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatDate(value: Date | null): string {
  if (!value) return 'Recent';
  return value.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
